
import React, { useState, useRef } from 'react';
import { X, Calendar, Store, Layers, ChevronDown, Paperclip, Trash, Check, Loader2, BrickWall, HardHat, FileText, FileBarChart, Tag, Lock, AlertCircle } from 'lucide-react';
import { Expense, Project, ExpenseCategory, PlanTier } from '../types';
import { currencyMask, parseCurrency, formatCurrency } from '../utils/formatters';
import { compressImage } from '../services/imageService';

interface ExpenseModalProps {
  project: Project;
  editingExpenseId: string | null;
  onClose: () => void;
  onSave: (data: Partial<Expense>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  actionLoading: boolean;
  planTier?: PlanTier;
  onUpgrade?: () => void;
}

export const ExpenseModal: React.FC<ExpenseModalProps> = ({ 
  project, editingExpenseId, onClose, onSave, onDelete, actionLoading, planTier = 'trial', onUpgrade
}) => {
  const isEditing = !!editingExpenseId;
  const expenseToEdit = isEditing ? project.expenses.find(e => e.id === editingExpenseId) : null;
  const canUpload = planTier !== 'trial';
  
  const [displayValue, setDisplayValue] = useState(expenseToEdit ? currencyMask(expenseToEdit.amount.toFixed(2).replace(".", "")) : "R$ 0,00");
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Partial<Expense>>(expenseToEdit || {
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    supplier: '',
    category: ExpenseCategory.MATERIALS,
    stageId: project.stages[0]?.id || '',
    isPaid: true,
    installments: 1,
    receiptUrl: ''
  });

  const receiptInputRef = useRef<HTMLInputElement>(null);

  const categories = [
     { id: ExpenseCategory.MATERIALS, icon: BrickWall, label: 'Materiais', color: 'bg-emerald-100 text-emerald-700' },
     { id: ExpenseCategory.LABOR, icon: HardHat, label: 'Mão de Obra', color: 'bg-blue-100 text-blue-700' },
     { id: ExpenseCategory.PROJECTS, icon: FileText, label: 'Projetos', color: 'bg-purple-100 text-purple-700' },
     { id: ExpenseCategory.DOCUMENTATION, icon: FileBarChart, label: 'Doc', color: 'bg-amber-100 text-amber-700' },
     { id: ExpenseCategory.OTHERS, icon: Tag, label: 'Outros', color: 'bg-slate-100 text-slate-700' },
  ];

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     const masked = currencyMask(e.target.value);
     setDisplayValue(masked);
     setFormData({ ...formData, amount: parseCurrency(masked) });
     if (validationError) setValidationError(null);
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
       try {
          if(file.type.startsWith('image/')) {
             const compressed = await compressImage(file, 1024, 0.7);
             setFormData({ ...formData, receiptUrl: compressed });
          } else {
             const reader = new FileReader();
             reader.onloadend = () => {
                setFormData({ ...formData, receiptUrl: reader.result as string });
             };
             reader.readAsDataURL(file);
          }
       } catch(err) {
          console.error("[ExpenseModal] Error processing receipt:", err);
       }
    }
  };

  const handleAttachmentClick = () => {
      if (canUpload) receiptInputRef.current?.click();
      else onUpgrade && onUpgrade();
  };

  const validate = (): boolean => {
      if (!formData.description || formData.description.length < 3) {
          setValidationError("A descrição deve ter pelo menos 3 caracteres.");
          return false;
      }
      if (!formData.amount || formData.amount <= 0) {
          setValidationError("O valor deve ser maior que zero.");
          return false;
      }
      return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
        onSave(formData).catch(err => {
            setValidationError("Erro ao salvar. Verifique sua conexão.");
            console.error(err);
        });
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div className="bg-white dark:bg-slate-950 w-full sm:max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 sm:p-8 shadow-2xl relative overflow-hidden flex flex-col max-h-[95vh]" onClick={e => e.stopPropagation()}>
         
         <div className="flex justify-between items-center mb-6">
           <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 rounded-full">
              <div className="size-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Novo Lançamento</span>
           </div>
           <button onClick={onClose} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-full hover:bg-slate-100 transition-colors"><X size={20} className="text-slate-500"/></button>
         </div>
         
         <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto no-scrollbar space-y-6 pb-2">
            
            <div className="text-center py-4">
               <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Qual o valor total?</label>
               <input 
                  type="tel" 
                  required 
                  autoFocus
                  className="w-full bg-transparent text-center text-5xl sm:text-6xl font-black text-emerald-600 dark:text-emerald-400 outline-none placeholder-emerald-200/50 caret-emerald-500 tracking-tighter"
                  value={displayValue}
                  onChange={handleAmountChange}
                  placeholder="R$ 0,00"
               />
            </div>

            {validationError && (
                <div className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-rose-100 dark:border-rose-900/30 animate-shake">
                    <AlertCircle size={18} /> {validationError}
                </div>
            )}

            <div className="space-y-4">
               <div className="bg-slate-50 dark:bg-slate-900 p-1.5 rounded-2xl flex border border-slate-100 dark:border-slate-800">
                  {categories.map(cat => (
                     <button
                        key={cat.id}
                        type="button"
                        onClick={() => setFormData({...formData, category: cat.id})}
                        className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${formData.category === cat.id ? 'bg-white dark:bg-slate-800 shadow-md scale-105' : 'opacity-50 hover:opacity-100'}`}
                     >
                        <cat.icon size={18} className={formData.category === cat.id ? 'text-emerald-600' : 'text-slate-400'} strokeWidth={2.5}/>
                        <span className={`text-[8px] font-black uppercase mt-1 ${formData.category === cat.id ? 'text-emerald-700' : 'text-slate-400'}`}>{cat.label}</span>
                     </button>
                  ))}
               </div>

               <input 
                  required 
                  className="w-full bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-100 dark:border-slate-800 text-lg placeholder:text-slate-400" 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})} 
                  placeholder="O que você comprou?" 
               />
            </div>

            <div className="grid grid-cols-2 gap-3">
               <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Data da Compra</label>
                  <div className="relative">
                     <input 
                        type="date" 
                        required 
                        className="w-full bg-slate-50 dark:bg-slate-900 p-3 pl-10 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-100 dark:border-slate-800" 
                        value={formData.date} 
                        onChange={e => setFormData({...formData, date: e.target.value})} 
                     />
                     <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
               </div>
               
               <div className="space-y-1">
                   <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Fornecedor</label>
                   <div className="relative">
                      <input 
                         className="w-full bg-slate-50 dark:bg-slate-900 p-3 pl-10 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-100 dark:border-slate-800" 
                         value={formData.supplier} 
                         onChange={e => setFormData({...formData, supplier: e.target.value})} 
                         placeholder="Loja..." 
                      />
                      <Store size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                   </div>
               </div>

               <div className="col-span-2 space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Etapa da Obra</label>
                  <div className="relative">
                     <select 
                        className="w-full bg-slate-50 dark:bg-slate-900 p-3 pl-10 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-100 dark:border-slate-800 appearance-none" 
                        value={formData.stageId} 
                        onChange={e => setFormData({...formData, stageId: e.target.value})}
                     >
                        {project.stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                     </select>
                     <Layers size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                     <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
               </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 space-y-4">
                <div className="flex items-center justify-between">
                   <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Condição de Pagamento</span>
                   <div className="flex items-center gap-2">
                      <button 
                         type="button" 
                         onClick={() => setFormData({...formData, installments: 1})}
                         className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${formData.installments === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}
                      >
                         À Vista
                      </button>
                      <button 
                         type="button" 
                         onClick={() => setFormData({...formData, installments: Math.max(2, formData.installments || 2)})}
                         className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${formData.installments && formData.installments > 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}
                      >
                         Parcelado
                      </button>
                   </div>
                </div>
                
                {formData.installments && formData.installments > 1 && (
                   <div className="animate-fadeIn">
                      <input 
                         type="range" 
                         min="2" max="24" 
                         value={formData.installments} 
                         onChange={e => setFormData({...formData, installments: parseInt(e.target.value)})}
                         className="w-full accent-emerald-600 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer mb-2"
                      />
                      <div className="flex justify-between items-center text-xs font-bold text-slate-600 dark:text-slate-300">
                         <span>{formData.installments}x Parcelas</span>
                         <span>{formatCurrency((formData.amount || 0) / formData.installments)} / mês</span>
                      </div>
                   </div>
                )}

                <div className="h-px bg-slate-200 dark:bg-slate-800"></div>

                <div className="flex items-center justify-between">
                   <div className="flex items-center gap-2">
                       <input 
                         type="checkbox" 
                         id="isPaid" 
                         className="w-5 h-5 accent-emerald-600 rounded-md" 
                         checked={formData.isPaid} 
                         onChange={e => setFormData({...formData, isPaid: e.target.checked})} 
                      />
                       <label htmlFor="isPaid" className="text-xs font-bold text-slate-700 dark:text-slate-300">Pagamento já realizado?</label>
                   </div>
                   
                   <div>
                      <input type="file" ref={receiptInputRef} className="hidden" accept="image/*,.pdf" onChange={handleReceiptUpload} />
                      <button 
                         type="button"
                         onClick={handleAttachmentClick}
                         className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-all ${formData.receiptUrl ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                      >
                         {canUpload ? <Paperclip size={14} /> : <Lock size={14} />}
                         {formData.receiptUrl ? 'Anexado' : (canUpload ? 'Anexar' : 'Anexar')}
                      </button>
                   </div>
                </div>
            </div>

            <div className="pt-2 flex gap-3">
               {isEditing && (
                  <button type="button" onClick={() => onDelete(editingExpenseId!)} className="p-4 bg-red-50 text-red-500 rounded-2xl hover:bg-red-100 transition-colors">
                     <Trash size={20} />
                  </button>
               )}
               <button type="submit" disabled={!formData.amount || actionLoading} className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-emerald-700 shadow-xl shadow-emerald-600/20 disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-2">
                  {actionLoading ? <Loader2 className="animate-spin" /> : <Check size={20} strokeWidth={3} />}
                  {isEditing ? 'Salvar Alterações' : 'Lançar Gasto'}
               </button>
            </div>

         </form>
      </div>
    </div>
  );
};
